import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {getSortedKeysOnValue} from '@Utils/object.util';
import type {Props as ReactModalProps} from 'react-modal';
import type {RootState} from '@/store';
import {POSTER_PUSH_MODAL_IDS} from '@Components/poster-editor/components/post-poster-modal-spacer/post-poster-modal-spacer.types';

export interface ControllableModalStorage extends Omit<ReactModalProps, 'appElement' | 'isOpen'> {
  panelProps?: Record<string, any>;
  animateModalClose?: boolean;
  isDismissible?: boolean;
  isOpen?: boolean;
  order?: number;
}

interface OpenPanelInModalProps {
  panelProps: Record<string, any>;
  isDismissible?: boolean;
  modalId: string;
}

export interface ModalStorage extends ControllableModalStorage {
  isOpen: boolean;
  panelProps: Record<string, any>;
  isDismissible: boolean;
  animateModalClose: boolean;
  order: number;
  height?: number;
  disableOutsideClickDismiss?: boolean;
}

interface UpdateModalHeightActions {
  modalId: string;
  height: number;
}

interface UpdateModalPanelPropsAction {
  modalId: string;
  panelProps: Record<string, any>;
}

interface StateProps {
  modalsHashmap: Record<string, ModalStorage>;
  keyboardShift: number;
}

const initialState: StateProps = {
  modalsHashmap: {},
  keyboardShift: 0,
};

export const modalContainerSlice = createSlice({
  name: 'modalContainer',
  initialState,
  reducers: {
    openPanelInModal: (state, action: PayloadAction<OpenPanelInModalProps>) => {
      const {modalId} = action.payload;
      state.modalsHashmap[modalId] = {
        ...state.modalsHashmap[modalId],
        order: 999,
        isDismissible: action.payload.isDismissible !== undefined ? action.payload.isDismissible : true,
        animateModalClose: true,
        isOpen: true,
        disableOutsideClickDismiss: false,
        panelProps: {...action.payload.panelProps},
      };
      updateOrderOfModals(state.modalsHashmap);
    },
    closeModal: (state, action: PayloadAction<string>) => {
      const modalData = state.modalsHashmap[action.payload];
      if (modalData) {
        modalData.isOpen = false;
      }
    },
    closeAllModals: (state) => {
      for (const [, modalData] of Object.entries(state.modalsHashmap)) {
        modalData.isOpen = false;
      }
    },
    updateHeightForModal: (state, action: PayloadAction<UpdateModalHeightActions>) => {
      state.modalsHashmap[action.payload.modalId].height = action.payload.height;
    },
    updateKeyboardShift: (state, action: PayloadAction<number>) => {
      state.keyboardShift = action.payload;
    },
    updateModalDisableOutsideClickDismiss: (state, action: PayloadAction<{modalId: string; disableOutsideClickDismiss: boolean}>) => {
      const {modalId, disableOutsideClickDismiss} = action.payload;
      if (state.modalsHashmap[modalId]) {
        state.modalsHashmap[modalId].disableOutsideClickDismiss = disableOutsideClickDismiss;
      }
    },
    updatePanelPropsForModal: (state, action: PayloadAction<UpdateModalPanelPropsAction>): void => {
      if (!state.modalsHashmap[action.payload.modalId]?.panelProps) {
        return;
      }

      state.modalsHashmap[action.payload.modalId].panelProps = action.payload.panelProps;
    },
  },
});

const updateOrderOfModals = (modalsHashmap: Record<string, ModalStorage>): void => {
  const modalsOrder: Record<string, number> = {};

  for (const [modalId, modalData] of Object.entries(modalsHashmap)) {
    modalsOrder[modalId] = modalData.order;
  }

  const sortedModalIds = getSortedKeysOnValue(modalsOrder);

  for (let i = 0; i < sortedModalIds.length; i++) {
    modalsHashmap[sortedModalIds[i]].order = i + 1;
  }
};

export const checkIfModalRootShouldHideFromStore = (state: RootState, panelId: string): boolean => {
  const isThisPanelHidden = !!state.panels.panelHashmap[panelId]?.isHidden;
  if (isThisPanelHidden) {
    return !!state.panels.panelHashmap[panelId]?.hideOnClose;
  }

  return false;
};

export const {openPanelInModal, updateModalDisableOutsideClickDismiss, closeModal, closeAllModals, updateHeightForModal, updateKeyboardShift, updatePanelPropsForModal} =
  modalContainerSlice.actions;
export const modalContainerReducer = modalContainerSlice.reducer;

export const isModalOpen = (state: RootState): boolean => {
  const panelHashmap = state.panels.panelHashmap;
  for (const [, modalData] of Object.entries(state.modals.modalsHashmap)) {
    if (modalData.isOpen) {
      const panelId = (modalData.panelProps?.panelId ?? modalData.panelProps?.id) as string;
      if (!(modalData.panelProps?.hideOnClose && panelHashmap[panelId]?.isHidden)) {
        return true;
      }
    }
  }
  return false;
};

export const isPosterPushModalOpen = (state: RootState): boolean => {
  for (const [modalId, modalData] of Object.entries(state.modals.modalsHashmap)) {
    if (modalData.isOpen && !POSTER_PUSH_MODAL_IDS[modalId] && modalData.panelProps?.hideOnClose !== true) {
      return true;
    }
  }
  return false;
};

